import React, { useState, useEffect } from 'react';
import { ICONS } from '../constants';
import { useTranslation } from '../hooks/useTranslation';
import Input from './ui/Input';
import ConfirmationModal from './ui/ConfirmationModal';
import { getUserPreferences, subscribeUserPreferences, updateUserPreferences } from '../utils/userPreferences';

interface ThemeEditorProps {
  isOpen: boolean;
  onClose: () => void;
  onThemeChange: (theme: any) => void;
}

interface ThemeColors {
  primary: string;
  secondary: string;
  accent: string;
  background: string;
  surface: string;
  text: string;
  textSecondary: string;
  border: string;
  success: string;
  warning: string;
  error: string;
  info: string;
  gray50: string;
  gray100: string;
  gray200: string;
  gray300: string;
  gray400: string;
  gray500: string;
  gray600: string;
  gray700: string;
  gray800: string;
  gray900: string;
}

const ThemeEditor: React.FC<ThemeEditorProps> = ({ isOpen, onClose, onThemeChange }) => {
  const { t } = useTranslation();
  const [theme, setTheme] = useState<ThemeColors>({
    primary: '#6366f1',
    secondary: '#8b5cf6',
    accent: '#06b6d4',
    background: '#111827',
    surface: '#1f2937',
    text: '#f9fafb',
    textSecondary: '#d1d5db',
    border: '#374151',
    success: '#10b981',
    warning: '#f59e0b',
    error: '#ef4444',
    info: '#3b82f6',
    gray50: '#f9fafb',
    gray100: '#f3f4f6',
    gray200: '#e5e7eb',
    gray300: '#d1d5db',
    gray400: '#9ca3af',
    gray500: '#6b7280',
    gray600: '#4b5563',
    gray700: '#374151',
    gray800: '#1f2937',
    gray900: '#111827'
  });

  const [isDarkMode, setIsDarkMode] = useState(true);
  const [resetConfirmModal, setResetConfirmModal] = useState(false);

  const applyThemeToDOM = (themeColors: ThemeColors) => {
    const root = document.documentElement;
    root.style.setProperty('--color-primary', themeColors.primary);
    root.style.setProperty('--color-secondary', themeColors.secondary);
    root.style.setProperty('--color-accent', themeColors.accent);
    root.style.setProperty('--color-background', themeColors.background);
    root.style.setProperty('--color-surface', themeColors.surface);
    root.style.setProperty('--color-text', themeColors.text);
    root.style.setProperty('--color-text-secondary', themeColors.textSecondary);
    root.style.setProperty('--color-border', themeColors.border);
    root.style.setProperty('--color-success', themeColors.success);
    root.style.setProperty('--color-warning', themeColors.warning);
    root.style.setProperty('--color-error', themeColors.error);
    root.style.setProperty('--color-info', themeColors.info);
    root.style.setProperty('--color-gray-50', themeColors.gray50);
    root.style.setProperty('--color-gray-100', themeColors.gray100);
    root.style.setProperty('--color-gray-200', themeColors.gray200);
    root.style.setProperty('--color-gray-300', themeColors.gray300);
    root.style.setProperty('--color-gray-400', themeColors.gray400);
    root.style.setProperty('--color-gray-500', themeColors.gray500);
    root.style.setProperty('--color-gray-600', themeColors.gray600);
    root.style.setProperty('--color-gray-700', themeColors.gray700);
    root.style.setProperty('--color-gray-800', themeColors.gray800);
    root.style.setProperty('--color-gray-900', themeColors.gray900);
  };

  useEffect(() => {
    if (isOpen) {
      // Load current theme from localStorage
      const savedTheme = localStorage.getItem('spellbook_custom_theme');
      const savedMode = localStorage.getItem('spellbook_theme');
      
      if (savedTheme) {
        const parsedTheme = JSON.parse(savedTheme);
        setTheme(parsedTheme);
        // Apply loaded theme
        applyThemeToDOM(parsedTheme);
      }
      
      if (savedMode) {
        setIsDarkMode(savedMode === 'dark');
      }
    }
  }, [isOpen]);

  const handleColorChange = (key: keyof ThemeColors, value: string) => {
    const newTheme = { ...theme, [key]: value };
    setTheme(newTheme);
    // Apply immediately for live preview
    applyThemeToDOM(newTheme);
  };

  const handleModeToggle = () => {
    const newMode = !isDarkMode;
    setIsDarkMode(newMode);
    document.documentElement.className = newMode ? 'dark' : 'light';
    updateUserPreferences({ theme: newMode ? 'dark' : 'light' });
    
    // Apply light or dark theme colors
    if (newMode) {
      // Dark theme - apply current theme
      applyThemeToDOM(theme);
    } else {
      // Light theme - apply light colors
      const lightTheme: ThemeColors = {
        ...theme,
        background: '#f9fafb',
        surface: '#ffffff',
        text: '#111827',
        textSecondary: '#6b7280',
        border: '#e5e7eb',
        gray50: '#f9fafb',
        gray100: '#f3f4f6',
        gray200: '#e5e7eb',
        gray300: '#d1d5db',
        gray400: '#9ca3af',
        gray500: '#6b7280',
        gray600: '#4b5563',
        gray700: '#f3f4f6',
        gray800: '#e5e7eb',
        gray900: '#d1d5db'
      };
      applyThemeToDOM(lightTheme);
    }
  };

  const handleSave = () => {
    updateUserPreferences({
      customTheme: theme as unknown as Record<string, unknown>,
      theme: isDarkMode ? 'dark' : 'light',
    });
    
    // Apply theme to DOM
    applyThemeToDOM(theme);
    
    // Notify parent
    onThemeChange(theme);
    
    // Show success message
    alert('Theme saved successfully! Refresh the page to see all changes.');
    
    // Close modal
    onClose();
  };

  const handleReset = () => {
    setResetConfirmModal(true);
  };
  
  const confirmReset = () => {
    setResetConfirmModal(false);
    
    const defaultTheme: ThemeColors = {
      primary: '#6366f1',
      secondary: '#8b5cf6',
      accent: '#06b6d4',
      background: '#111827',
      surface: '#1f2937',
      text: '#f9fafb',
      textSecondary: '#d1d5db',
      border: '#374151',
      success: '#10b981',
      warning: '#f59e0b',
      error: '#ef4444',
      info: '#3b82f6',
      gray50: '#f9fafb',
      gray100: '#f3f4f6',
      gray200: '#e5e7eb',
      gray300: '#d1d5db',
      gray400: '#9ca3af',
      gray500: '#6b7280',
      gray600: '#4b5563',
      gray700: '#374151',
      gray800: '#1f2937',
      gray900: '#111827'
    };
    
    setTheme(defaultTheme);
    applyThemeToDOM(defaultTheme);
    setIsDarkMode(true);
    
    updateUserPreferences({ customTheme: null, theme: 'dark' });
  };

  const colorGroups = [
    {
      title: 'Основные цвета',
      colors: [
        { key: 'primary', label: 'Основной', description: 'Кнопки, ссылки, акценты' },
        { key: 'secondary', label: 'Вторичный', description: 'Дополнительные элементы' },
        { key: 'accent', label: 'Акцент', description: 'Выделение, уведомления' }
      ]
    },
    {
      title: 'Фон и поверхности',
      colors: [
        { key: 'background', label: 'Фон', description: 'Основной фон приложения' },
        { key: 'surface', label: 'Поверхность', description: 'Карточки, модальные окна' },
        { key: 'border', label: 'Границы', description: 'Разделители, рамки' }
      ]
    },
    {
      title: 'Текст',
      colors: [
        { key: 'text', label: 'Основной текст', description: 'Заголовки, основной контент' },
        { key: 'textSecondary', label: 'Вторичный текст', description: 'Описания, подписи' }
      ]
    },
    {
      title: 'Состояния',
      colors: [
        { key: 'success', label: 'Успех', description: 'Успешные действия' },
        { key: 'warning', label: 'Предупреждение', description: 'Предупреждения' },
        { key: 'error', label: 'Ошибка', description: 'Ошибки, удаление' },
        { key: 'info', label: 'Информация', description: 'Информационные сообщения' }
      ]
    },
    {
      title: 'Дополнительные оттенки серого',
      colors: [
        { key: 'gray50', label: 'Серый 50', description: 'Очень светлый фон' },
        { key: 'gray100', label: 'Серый 100', description: 'Светлый фон' },
        { key: 'gray200', label: 'Серый 200', description: 'Границы, разделители' },
        { key: 'gray300', label: 'Серый 300', description: 'Иконки, подсказки' },
        { key: 'gray400', label: 'Серый 400', description: 'Вторичный текст' },
        { key: 'gray500', label: 'Серый 500', description: 'Нейтральный текст' },
        { key: 'gray600', label: 'Серый 600', description: 'Темные элементы' },
        { key: 'gray700', label: 'Серый 700', description: 'Карточки, панели' },
        { key: 'gray800', label: 'Серый 800', description: 'Sidebar, header' },
        { key: 'gray900', label: 'Серый 900', description: 'Основной фон' }
      ]
    }
  ];

  if (!isOpen) return null;

  return (
    <div className="w-full">
      <div className="bg-gray-custom-700 rounded-lg p-6 w-full" style={{ backgroundColor: 'var(--color-gray-700)' }}>

        <div className="space-y-6">
          {/* Theme Mode Toggle */}
          <div className="p-4 rounded-lg border" style={{ 
            backgroundColor: 'var(--color-surface)', 
            borderColor: 'var(--color-border)' 
          }}>
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium mb-1" style={{ color: 'var(--color-text)' }}>Режим темы</h3>
                <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>Выберите светлую или темную тему</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={isDarkMode}
                  onChange={handleModeToggle}
                  className="sr-only peer"
                />
                <div className={`w-11 h-6 rounded-full peer transition-colors ${
                  isDarkMode ? 'bg-indigo-600' : 'bg-gray-400'
                }`}>
                  <div className={`absolute top-[2px] left-[2px] h-5 w-5 rounded-full transition-transform ${
                    isDarkMode ? 'translate-x-6 bg-white' : 'translate-x-1 bg-gray-200'
                  }`}></div>
                </div>
              </label>
            </div>
          </div>

          {/* Color Groups */}
          {colorGroups.map((group, groupIndex) => (
            <div key={groupIndex} className="space-y-4">
              <h3 className="text-lg font-medium" style={{ color: 'var(--color-text)' }}>{group.title}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {group.colors.map((color) => (
                  <div key={color.key} className="p-4 rounded-lg border" style={{ 
                    backgroundColor: 'var(--color-surface)', 
                    borderColor: 'var(--color-border)' 
                  }}>
                    <div className="flex items-center space-x-3 mb-2">
                      <input
                        type="color"
                        value={theme[color.key as keyof ThemeColors]}
                        onChange={(e) => handleColorChange(color.key as keyof ThemeColors, e.target.value)}
                        className="w-8 h-8 rounded border cursor-pointer"
                        style={{ borderColor: 'var(--color-border)' }}
                      />
                      <div>
                        <div className="text-sm font-medium" style={{ color: 'var(--color-text)' }}>{color.label}</div>
                        <div className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>{color.description}</div>
                      </div>
                    </div>
                    <Input
                      label=""
                      value={theme[color.key as keyof ThemeColors]}
                      onChange={(e) => handleColorChange(color.key as keyof ThemeColors, e.target.value)}
                      placeholder="#000000"
                      className="text-xs font-mono"
                      style={{ 
                        backgroundColor: 'var(--color-gray-700)', 
                        color: 'var(--color-text)',
                        borderColor: 'var(--color-border)'
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}

          {/* Preview */}
          <div className="p-4 rounded-lg border" style={{ 
            backgroundColor: 'var(--color-surface)', 
            borderColor: 'var(--color-border)' 
          }}>
            <h3 className="text-lg font-medium mb-4" style={{ color: 'var(--color-text)' }}>Предварительный просмотр</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 rounded-lg" style={{ backgroundColor: theme.surface, borderColor: theme.border, borderWidth: '1px' }}>
                <h4 className="text-lg font-semibold mb-2" style={{ color: theme.text }}>Заголовок</h4>
                <p className="text-sm mb-3" style={{ color: theme.textSecondary }}>Описание элемента</p>
                <div className="flex space-x-2">
                  <button className="px-3 py-1 rounded text-sm font-medium" style={{ backgroundColor: theme.primary, color: theme.text }}>
                    Основная кнопка
                  </button>
                  <button className="px-3 py-1 rounded text-sm font-medium border" style={{ borderColor: theme.border, color: theme.text }}>
                    Вторичная кнопка
                  </button>
                </div>
              </div>
              
              <div className="space-y-2">
                <div className="p-2 rounded text-sm" style={{ backgroundColor: theme.success, color: theme.text }}>
                  ✓ Успешное действие
                </div>
                <div className="p-2 rounded text-sm" style={{ backgroundColor: theme.warning, color: theme.text }}>
                  ⚠ Предупреждение
                </div>
                <div className="p-2 rounded text-sm" style={{ backgroundColor: theme.error, color: theme.text }}>
                  ✗ Ошибка
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3">
            <button
              onClick={handleReset}
              className="px-4 py-2 rounded-md font-semibold focus:outline-none focus:ring-2 transition-transform transform hover:scale-105"
              style={{ 
                backgroundColor: theme.surface,
                borderColor: theme.border,
                borderWidth: '1px',
                borderStyle: 'solid',
                color: theme.text
              }}
            >
              Сбросить
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 rounded-md font-semibold focus:outline-none focus:ring-2 transition-transform transform hover:scale-105"
              style={{ 
                backgroundColor: theme.primary,
                borderColor: theme.primary,
                borderWidth: '1px',
                borderStyle: 'solid',
                color: '#ffffff'
              }}
            >
              Сохранить
            </button>
          </div>
        </div>
      </div>
      
      {/* Reset Theme Confirmation Modal */}
      <ConfirmationModal
        isOpen={resetConfirmModal}
        onClose={() => setResetConfirmModal(false)}
        onConfirm={confirmReset}
        title={t('themeEditor.resetTitle') || 'Сбросить тему'}
        message={t('themeEditor.resetConfirm') || 'Сбросить все цвета к настройкам по умолчанию? Это действие нельзя отменить.'}
        confirmText={t('common.reset') || 'Сбросить'}
        cancelText={t('common.cancel') || 'Отмена'}
        type="warning"
      />
    </div>
  );
};

export default ThemeEditor;
